import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkDeleteFoldersOptions,
  CreateFolderOptions,
  CreateFolderResponse,
  Folder,
  FolderQuery,
  FoldersQueryBuilder,
  GenerateFolderDownloadUrlResponse,
  ListDeletedFoldersOptions,
  ListDeletedFoldersResponse,
  ListFoldersOptions,
  ListFoldersResponse,
  QueryFoldersResponse,
  SearchFoldersOptions,
  SearchFoldersResponse,
  UpdateFolder,
  UpdateFolderValidationErrors,
  bulkDeleteFolders as universalBulkDeleteFolders,
  bulkRestoreFoldersFromTrashBin as universalBulkRestoreFoldersFromTrashBin,
  createFolder as universalCreateFolder,
  generateFolderDownloadUrl as universalGenerateFolderDownloadUrl,
  getFolder as universalGetFolder,
  listDeletedFolders as universalListDeletedFolders,
  listFolders as universalListFolders,
  queryFolders as universalQueryFolders,
  searchFolders as universalSearchFolders,
  typedQueryFolders as universalTypedQueryFolders,
  updateFolder as universalUpdateFolder,
} from './media-site-media-v1-folder-folders.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/media' };

export function createFolder(httpClient: HttpClient): CreateFolderSignature {
  return (displayName: string, options?: CreateFolderOptions) =>
    universalCreateFolder(
      displayName,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateFolderSignature {
  /**
   * Creates a new folder in the Media Manager. <br/>
   *
   * Use the `parentFolderId` parameter to specify in which existing folder you want the new folder to be created.
   * If no folder is specified, the new folder is created in the `media-root` folder.
   * @param - Folder name that appears in the Media Manager.
   * @param - Options for specifying where to create a folder.
   */
  (displayName: string, options?: CreateFolderOptions): Promise<
    NonNullablePaths<
      CreateFolderResponse,
      | `folder._id`
      | `folder.displayName`
      | `folder.parentFolderId`
      | `folder.state`
      | `folder.siteId`,
      3
    >
  >;
}

export function getFolder(httpClient: HttpClient): GetFolderSignature {
  return (folderId: string) =>
    universalGetFolder(
      folderId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetFolderSignature {
  /**
   * Gets information from a specific folder in the Media Manager.
   * @param - Folder ID.
   * @returns Information about the folder.
   */
  (folderId: string): Promise<
    NonNullablePaths<
      Folder,
      `_id` | `displayName` | `parentFolderId` | `state` | `siteId`,
      2
    >
  >;
}

export function listFolders(httpClient: HttpClient): ListFoldersSignature {
  return (options?: ListFoldersOptions) =>
    universalListFolders(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListFoldersSignature {
  /**
   * Retrieves a list of folders in the Media Manager. To retrieve a list of folders within a specific folder in the Media Manager, specify the specific folder's ID in the `parentFolderId` parameter. If no folder is specified, the method retrieves a list of folders within the root folder of the Media Manager.
   * @param - Options to use when listing folders from the Media Manager.
   */
  (options?: ListFoldersOptions): Promise<
    NonNullablePaths<
      ListFoldersResponse,
      | `folders`
      | `folders.${number}._id`
      | `folders.${number}.displayName`
      | `folders.${number}.parentFolderId`
      | `folders.${number}.state`
      | `folders.${number}.siteId`,
      4
    >
  >;
}

export function searchFolders(httpClient: HttpClient): SearchFoldersSignature {
  return (options?: SearchFoldersOptions) =>
    universalSearchFolders(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchFoldersSignature {
  /**
   * Retrieves a list of up to 200 folders with the specified filtering, sorting, and cursor paging.
   *
   * If no parameters are specified, the method returns all folders in the `MEDIA_ROOT` folder.
   *
   * ## Defaults
   *
   * Search Folders has the following default setting, which you can override:
   * + Sorted by `updatedDate` in descending order.
   *
   * ## Filters
   *
   * When using filters for dates, you must use
   * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   *
   * ## See also
   *
   * To learn about working with *Search* methods, see
   * [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) and
   * [Sorting and Paging](https://dev.wix.com/docs/rest/articles/get-started/sorting-and-paging).
   * @param - Options specifying which folders to search.
   */
  (options?: SearchFoldersOptions): Promise<
    NonNullablePaths<
      SearchFoldersResponse,
      | `folders`
      | `folders.${number}._id`
      | `folders.${number}.displayName`
      | `folders.${number}.parentFolderId`
      | `folders.${number}.state`
      | `folders.${number}.siteId`,
      4
    >
  >;
}

export function updateFolder(httpClient: HttpClient): UpdateFolderSignature {
  return (_id: string, folder: UpdateFolder) =>
    universalUpdateFolder(
      _id,
      folder,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateFolderSignature {
  /**
   * Updates a folder. <br />
   *
   * You can use the `parentFolderId` parameter to move a folder from its current parent folder to a different parent folder.
   * @param - Folder ID. Generated when a folder is created in the Media Manager.
   * @param - Folder to update.
   * @returns Information about the updated folder.
   */
  (_id: string, folder: UpdateFolder): Promise<
    NonNullablePaths<
      Folder,
      `_id` | `displayName` | `parentFolderId` | `state` | `siteId`,
      2
    > & {
      __validationErrorsType?: UpdateFolderValidationErrors;
    }
  >;
}

export function generateFolderDownloadUrl(
  httpClient: HttpClient
): GenerateFolderDownloadUrlSignature {
  return (folderId: string) =>
    universalGenerateFolderDownloadUrl(
      folderId,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateFolderDownloadUrlSignature {
  /**
   * Generates a URL for downloading a compressed file containing a specific folder in the Media Manager. <br/>
   *
   * The compressed file can contain sub-folders, and up to 1000 files.
   * @param - Folder ID.
   */
  (folderId: string): Promise<
    NonNullablePaths<GenerateFolderDownloadUrlResponse, `downloadUrl`, 2>
  >;
}

export function bulkDeleteFolders(
  httpClient: HttpClient
): BulkDeleteFoldersSignature {
  return (folderIds: string[], options?: BulkDeleteFoldersOptions) =>
    universalBulkDeleteFolders(
      folderIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteFoldersSignature {
  /**
   * Temporarily deletes the specified folders from the Media Manager. <br/>
   *
   * The deleted folders are moved to the Media Manager's `trash-root` folder (trash bin) unless permanently deleted. To permanently delete folders, specify the `permanent` parameter with the value `true`. Permanently deleting folders isn't reversible, so make sure that the files in these folders aren't being used in a site or in any other way as the files will no longer be accessible.
   *
   * Note the following:
   * * When a folder is deleted, the files in that folder are deleted.
   * * The specified folders can be from different parent folders.
   * * Moving multiple folders at once is an asynchronous action, and may take time for the changes to appear in the Media Manager.
   * * Attempting to delete folders that are already in the trash bin doesn't result in an error.
   * * If a site contains files from a deleted media folder, the files still appear on the site as the deleted folder is still in the Media Manager (in the trash bin).
   * * You can also use the [Bulk Restore Folders From Trash Bin](https://dev.wix.com/docs/rest/assets/media/media-manager/folders/bulk-restore-folders-from-trash-bin) method to restore folders from the Media Manager's trash bin.
   * @param - IDs of the folders to move to the Media Manager's trash bin.
   * @param - Options to use when deleting folders.
   */
  (folderIds: string[], options?: BulkDeleteFoldersOptions): Promise<void>;
}

export function bulkRestoreFoldersFromTrashBin(
  httpClient: HttpClient
): BulkRestoreFoldersFromTrashBinSignature {
  return (folderIds: string[]) =>
    universalBulkRestoreFoldersFromTrashBin(
      folderIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkRestoreFoldersFromTrashBinSignature {
  /**
   * Restores the specified folders from the Media Manager's trash bin, and moves them to their original locations in the Media Manager.
   * @param - IDs of the folders to restore from the Media Manager's trash bin.
   */
  (folderIds: string[]): Promise<void>;
}

export function listDeletedFolders(
  httpClient: HttpClient
): ListDeletedFoldersSignature {
  return (options?: ListDeletedFoldersOptions) =>
    universalListDeletedFolders(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDeletedFoldersSignature {
  /**
   * Retrieves a list of folders in the Media Manager's trash bin.
   * @param - Options to use when listing deleted folders from the trash bin.
   */
  (options?: ListDeletedFoldersOptions): Promise<
    NonNullablePaths<
      ListDeletedFoldersResponse,
      | `folders`
      | `folders.${number}._id`
      | `folders.${number}.displayName`
      | `folders.${number}.parentFolderId`
      | `folders.${number}.state`
      | `folders.${number}.siteId`,
      4
    >
  >;
}

export function queryFolders(httpClient: HttpClient): QueryFoldersSignature {
  return () =>
    universalQueryFolders(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryFoldersSignature {
  /**
   * Retrieves a list of folders in the Media Manager, given the provided [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging), filtering, and sorting.
   *
   * Up to 200 folders can be returned per request.
   *
   * To learn about working with *Query* methods, see
   * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and
   * [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging)
   */
  (): FoldersQueryBuilder;
}

export function typedQueryFolders(
  httpClient: HttpClient
): TypedQueryFoldersSignature {
  return (query: FolderQuery) =>
    universalTypedQueryFolders(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryFoldersSignature {
  /** */
  (query: FolderQuery): Promise<
    NonNullablePaths<
      QueryFoldersResponse,
      | `folders`
      | `folders.${number}._id`
      | `folders.${number}.displayName`
      | `folders.${number}.parentFolderId`
      | `folders.${number}.state`
      | `folders.${number}.siteId`,
      4
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddressLocation,
  BulkDeleteFoldersOptions,
  BulkDeleteFoldersRequest,
  BulkDeleteFoldersResponse,
  BulkRestoreFoldersFromTrashBinRequest,
  BulkRestoreFoldersFromTrashBinResponse,
  CommonSortOrder,
  CommonSorting,
  CreateFolderOptions,
  CreateFolderRequest,
  CreateFolderResponse,
  CursorPaging,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Folder,
  FolderQuerySpec,
  FoldersQueryBuilder,
  FoldersQueryResult,
  GenerateFolderDownloadUrlRequest,
  GenerateFolderDownloadUrlResponse,
  GetFolderRequest,
  GetFolderResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListDeletedFoldersOptions,
  ListDeletedFoldersRequest,
  ListDeletedFoldersResponse,
  ListFoldersOptions,
  ListFoldersRequest,
  ListFoldersResponse,
  MessageEnvelope,
  Namespace,
  PagingMetadataV2,
  QueryFoldersRequest,
  QueryFoldersResponse,
  RestoreInfo,
  RootFolder,
  SearchFoldersOptions,
  SearchFoldersRequest,
  SearchFoldersResponse,
  SortOrder,
  Sorting,
  State,
  UnsupportedRequestValueError,
  UpdateFolder,
  UpdateFolderRequest,
  UpdateFolderResponse,
  WebhookIdentityType,
  utils,
} from './media-site-media-v1-folder-folders.universal.js';
